import { useState, useRef, Fragment } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery, useIsSmallScreenFalse, getFullPathFromUrl } from 'packages/core'
import { useUiActionsByResource } from 'hooks'
import { EidWorkflow, Dropdown, Loader } from 'packages/eid-ui'

import config from 'config'
import { MenuItem, Button, Backdrop, styled, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import { isApplicationAzureApp } from 'utils'

const useBlueStyles = makeStyles((theme) =>
    createStyles({
        root: (dropdownOpen) => ({
            position: 'relative',
            height: '48px',
            minWidth: '48px',
            padding: '0px 0px 0px 0px',
            borderRadius: '4px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',

            textTransform: 'capitalize',

            backgroundColor: theme?.palette?.primary?.main,
            border: 'solid 1px #d8d8dd',

            zIndex: dropdownOpen ? '1202' : '0',
            '&:hover': {
                backgroundColor: theme?.palette?.primary?.main,
                color: 'white',
                cursor: 'hand',
            },
        }),
        dropdownAnchor: {
            position: 'absolute',
            top: '0px',
            left: '0px',
        },
        noActionMessage: {
            padding: '0 10px',
        },
        disabled: {
            backgroundColor: '#efeff1 !important',
            cursor: 'no-drop',
            '&:hover': {
                boxShadow: 'none',
            },
        },
    }),
)

const StyledBackdrop = styled(Backdrop)(() => ({
    zIndex: '1201',
}))

export const ActionsButton = ({ item }) => {
    const query = useQuery()
    const UI_ACTIONS_NOUN = 'ResourceAdmin'

    const UI_ACTIONS_VERB = isApplicationAzureApp(item?.applicationType)
        ? 'Azure'
        : 'NonAzure'

    const history = useHistory()
    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreenFalse()

    const disabled = item.isExpired
    const iconColor = disabled ? '#8b909a' : 'white'

    const [actionsButtonIconColor, setActionsButtonIconColor] =
        useState(iconColor)
    const [anchorEl, setAnchorEl] = useState(null)
    const buttonClasses = useBlueStyles(Boolean(anchorEl))

    const { data, isLoading } = useUiActionsByResource(
        item.id,
        UI_ACTIONS_NOUN,
        UI_ACTIONS_VERB,
        Boolean(anchorEl),
    )

    const openMenu = (event) => {
        setAnchorEl(event.currentTarget)
        setActionsButtonIconColor('white')
    }

    const anchorRef = useRef(null)

    const closeMenu = () => {
        setAnchorEl(null)
        setActionsButtonIconColor(iconColor)
    }

    const onActionClicked = (workflowName) => {
        query.set('workflowName', workflowName)
        query.set('SelectedResourceID', item.resourceId)
        query.set('returnUrl', getFullPathFromUrl())
        history.push(
            `${config.APP_SUBPATH}/applications/workflows?${query.toString()}`,
        )
    }

    return (
        <Fragment>
            <Button
                onClick={openMenu}
                onMouseEnter={() => {
                    setActionsButtonIconColor('white')
                }}
                onMouseLeave={() => {
                    !Boolean(anchorEl) && setActionsButtonIconColor(iconColor)
                }}
                classes={buttonClasses}
                disabled={disabled}
            >
                <Icon name="ActionsOutlined" color={actionsButtonIconColor} />
                <span
                    ref={anchorRef}
                    className={buttonClasses.dropdownAnchor}
                />
            </Button>
            <StyledBackdrop
                sx={{ color: '#fff' }}
                open={Boolean(anchorEl)}
                onClick={closeMenu}
            />

            <Dropdown
                anchorEl={anchorRef.current}
                showCone={!isSmallScreen}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                width={'auto'}
                listStyles={{
                    paddingBottom: '0px',
                    whiteSpace: 'normal',
                    '& > ul': {
                        paddingBottom: '7px',
                        paddingTop: '0px',
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                iconPosition={{
                    top: '-8px',
                    right: '13px',
                }}
                disableScrollLock={false}
            >
                {isLoading && <Loader height={40} />}
                {data && data.length > 0 ? (
                    <>
                        {data.map((d) =>
                            isSmallScreen ? (
                                <EidWorkflow
                                    key={d.id}
                                    baseEidUrl={config.BASE_EID_URL}
                                    title={d.friendlyName}
                                    workflowName={d.requestWorkflowName}
                                    workflowParams={[
                                        `SelectedResourceID=${item.resourceId}`,
                                    ]}
                                >
                                    <MenuItem
                                        onClick={closeMenu}
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {d.friendlyName}
                                    </MenuItem>
                                </EidWorkflow>
                            ) : (
                                <MenuItem
                                    key={d.id}
                                    onClick={() => {
                                        closeMenu()
                                        onActionClicked(d.requestWorkflowName)
                                    }}
                                >
                                    {d.friendlyName}
                                </MenuItem>
                            ),
                        )}
                    </>
                ) : data && data.length === 0 && !isLoading ? (
                    <MenuItem disabled>
                        <Typography className={buttonClasses.noActionMessage}>
                            {t('NoActionFound')}
                        </Typography>
                    </MenuItem>
                ) : (
                    <></>
                )}
            </Dropdown>
        </Fragment>
    )
}
